import { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Store, Loader2, Check, X } from 'lucide-react';
import api from '@/lib/api';

export default function Register() {
  const [form, setForm] = useState({ name: '', email: '', password: '', business_name: '', subdomain: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [subdomainAvailable, setSubdomainAvailable] = useState(null);
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get('ref') || '';

  const checkSubdomain = async (val) => {
    if (val.length < 3) { setSubdomainAvailable(null); return; }
    try {
      const res = await api.get(`/auth/check-subdomain/${val}`);
      setSubdomainAvailable(res.data.available);
    } catch { setSubdomainAvailable(null); }
  };

  const handleChange = (field, val) => {
    if (field === 'subdomain') {
      val = val.toLowerCase().replace(/[^a-z0-9-]/g, '');
      checkSubdomain(val);
    }
    setForm({ ...form, [field]: val });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (subdomainAvailable === false) { setError('Subdomain tidak tersedia'); return; }
    setLoading(true);
    try {
      await register({ ...form, role: 'tenant_owner', ...(refCode ? { ref: refCode } : {}) });
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Registrasi gagal');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="register-page">
      <Card className="w-full max-w-md shadow-lg border-slate-200">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-4">
            <img src="/logo.png" alt="Tokomoe" className="h-10 object-contain" />
          </div>
          <CardTitle className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans' }}>Daftar di Tokomoe</CardTitle>
          <CardDescription>Buat akun tenant dan mulai bisnis online</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {refCode && (
              <div className="bg-violet-50 border border-violet-200 text-violet-700 text-sm rounded-lg p-3 flex items-center gap-2">
                <Store size={16} /> Anda direferensikan oleh affiliate: <strong>{refCode}</strong>
              </div>
            )}
            {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3" data-testid="register-error">{error}</div>}
            <div>
              <Label>Nama Lengkap</Label>
              <Input value={form.name} onChange={(e) => handleChange('name', e.target.value)} placeholder="John Doe" required data-testid="register-name-input" className="mt-1" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => handleChange('email', e.target.value)} placeholder="john@example.com" required data-testid="register-email-input" className="mt-1" />
            </div>
            <div>
              <Label>Password</Label>
              <Input type="password" value={form.password} onChange={(e) => handleChange('password', e.target.value)} placeholder="Min 6 karakter" required data-testid="register-password-input" className="mt-1" />
            </div>
            <div>
              <Label>Nama Bisnis</Label>
              <Input value={form.business_name} onChange={(e) => handleChange('business_name', e.target.value)} placeholder="Toko Saya" required data-testid="register-business-input" className="mt-1" />
            </div>
            <div>
              <Label>Subdomain Store</Label>
              <div className="relative mt-1">
                <Input value={form.subdomain} onChange={(e) => handleChange('subdomain', e.target.value)} placeholder="tokosaya" required data-testid="register-subdomain-input" className="pr-10" />
                {subdomainAvailable !== null && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {subdomainAvailable ? <Check size={16} className="text-emerald-500" /> : <X size={16} className="text-red-500" />}
                  </div>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-1">{form.subdomain ? `${form.subdomain}.tokomoe.com` : 'subdomain.tokomoe.com'}</p>
            </div>
            <Button type="submit" className="w-full bg-[#2D6CDF] hover:bg-[#1A5BC4]" disabled={loading} data-testid="register-submit-btn">
              {loading ? <Loader2 size={18} className="animate-spin mr-2" /> : null}
              Daftar Sekarang
            </Button>
          </form>
          <div className="mt-6 text-center text-sm text-slate-500">
            Sudah punya akun?{' '}
            <Link to="/login" className="text-[#2D6CDF] font-medium hover:underline" data-testid="goto-login-link">Masuk</Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
